/**
 * Hook para cliente WebRTC de Telnyx en el navegador.
 * Usa un store singleton (telnyxWebRTCStore) para que la llamada no se corte al cambiar
 * de página ni al re-montar componentes (patrón usado en call centers).
 * Ver docs/guides/PLAN-VOZ-NAVEGADOR-WEBRTC.md
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getWebRTCState,
  subscribe,
  connect as storeConnect,
  disconnect as storeDisconnect,
  disconnectAndDisable as storeDisconnectAndDisable,
  hangup as storeHangup,
  clearActiveCallFromRemote as storeClearActiveCallFromRemote,
  isStorageEnabled,
  setDialContext as storeSetDialContext,
  toggleMute as storeToggleMute,
  toggleHold as storeToggleHold,
} from '@/lib/telnyxWebRTCStore';
import type { WebrtcCallUiPhase } from '@/lib/telnyxCallState';

type WebrtcStatus = 'idle' | 'connecting' | 'connected' | 'error';

interface UseTelnyxWebRTCResult {
  status: WebrtcStatus;
  error: string | null;
  activeCallId: string | null;
  webrtcUsername: string | null;
  callPhase: WebrtcCallUiPhase;
  callDirection: 'inbound' | 'outbound' | null;
  remoteDialNumber: string | null;
  /** Fila CRM enlazada (timbrado + fin de llamada remota). Solo la usa CallCenterProvider. */
  activeCrmCallId: string | null;
  /** `crm_calls.call_status` (webhook). Sirve para no mostrar “En llamada” hasta `answered` en el PSTN. */
  crmCallStatus: string | null;
  isMuted: boolean;
  isOnHold: boolean;
  setDialContext: (phone: string | null) => void;
  connect: () => Promise<void>;
  disconnect: () => void;
  disconnectAndDisable: () => void;
  hangup: () => void;
  clearActiveCallFromRemote: () => void;
  toggleMute: () => boolean;
  toggleHold: () => boolean;
}

export function useTelnyxWebRTC(): UseTelnyxWebRTCResult {
  const [state, setState] = useState(getWebRTCState);
  const initialMount = useRef(true);

  useEffect(() => {
    const unsub = subscribe((next) => setState(next));
    return unsub;
  }, []);

  // Solo al primer montar: si el usuario tenía "llamadas por navegador" activas, reconectar.
  // No desconectamos al desmontar: el cliente vive en el store y la llamada sigue activa al cambiar de página.
  useEffect(() => {
    if (!initialMount.current) return;
    initialMount.current = false;
    if (isStorageEnabled() && state.status === 'idle') {
      void storeConnect();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al montar
  }, []);

  const connect = useCallback(async () => {
    await storeConnect();
  }, []);

  const disconnect = useCallback(() => {
    storeDisconnect();
  }, []);

  const disconnectAndDisable = useCallback(() => {
    storeDisconnectAndDisable();
  }, []);

  const hangup = useCallback(() => {
    storeHangup();
  }, []);

  const clearActiveCallFromRemote = useCallback(() => {
    storeClearActiveCallFromRemote();
  }, []);

  const setDialContext = useCallback((phone: string | null) => {
    storeSetDialContext(phone);
  }, []);

  const toggleMute = useCallback(() => storeToggleMute(), []);
  const toggleHold = useCallback(() => storeToggleHold(), []);

  return {
    status: state.status,
    error: state.error,
    activeCallId: state.activeCallId,
    webrtcUsername: state.webrtcUsername,
    callPhase: state.callPhase,
    callDirection: state.callDirection,
    remoteDialNumber: state.remoteDialNumber,
    activeCrmCallId: state.activeCrmCallId,
    crmCallStatus: state.crmCallStatus,
    isMuted: state.isMuted,
    isOnHold: state.isOnHold,
    setDialContext,
    connect,
    disconnect,
    disconnectAndDisable,
    hangup,
    clearActiveCallFromRemote,
    toggleMute,
    toggleHold,
  };
}
